import { ref, computed, onUnmounted } from 'vue';
import { TimeUtils } from '@/utils/TimeUtils.js';
import { AudioService } from '@/services/AudioService.js';

/**
 * Composable for timer functionality
 * Handles timer state, lifecycle, and phase timing
 */
export function useTimer() {
    // Timer state
    const currentPhaseIndex = ref(0);
    const remainingTime = ref(0);
    const isRunning = ref(false);
    const hasStarted = ref(false);
    const phaseElapsedTimes = ref([]);
    
    // Private timer variables
    let intervalId = null;
    let phaseStartTime = null;
    let phasePausedTime = 0;
    
    // Audio service
    const audioService = new AudioService();

    // Computed properties
    const timeExpired = computed(() => TimeUtils.isTimeExpired(remainingTime.value));
    const totalElapsedTime = computed(() => 
        phaseElapsedTimes.value.reduce((sum, time) => sum + time, 0)
    );

    /**
     * Initialize timer for given phases
     * @param {Array} phases - Array of phase objects
     */
    const initializeTimer = (phases) => {
        if (!phases || phases.length === 0) return;
        
        currentPhaseIndex.value = 0;
        remainingTime.value = phases[0].maxTime;
        phaseElapsedTimes.value = new Array(phases.length).fill(0);
        isRunning.value = false;
        hasStarted.value = false;
        phasePausedTime = 0;
        
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    };

    /**
     * Start the timer
     * @param {Object} currentPhase - Current phase object
     */
    const startTimer = (currentPhase) => {
        if (!isRunning.value && currentPhase) {
            phaseStartTime = TimeUtils.getCurrentTimestamp() - phasePausedTime;
            isRunning.value = true;
            hasStarted.value = true;
            
            intervalId = setInterval(() => {
                const phaseElapsed = TimeUtils.millisecondsToSeconds(
                    TimeUtils.getCurrentTimestamp() - phaseStartTime
                );
                const newRemainingTime = currentPhase.maxTime - phaseElapsed;
                
                // Check for time expiration and play alert
                if (remainingTime.value >= 0 && newRemainingTime < 0) {
                    audioService.playSiren();
                }
                
                remainingTime.value = newRemainingTime;
                
                // Update phase elapsed time
                const currentPhaseElapsed = Math.max(0, phaseElapsed);
                phaseElapsedTimes.value[currentPhaseIndex.value] = currentPhaseElapsed;
            }, 100);
        }
    };

    /**
     * Pause the timer
     */
    const pauseTimer = () => {
        if (isRunning.value) {
            isRunning.value = false;
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
            phasePausedTime = TimeUtils.getCurrentTimestamp() - phaseStartTime;
        }
    };

    /**
     * Toggle timer (start/pause)
     * @param {Object} currentPhase - Current phase object
     */
    const toggleTimer = (currentPhase) => {
        if (isRunning.value) {
            pauseTimer();
        } else {
            startTimer(currentPhase);
        }
    };

    /**
     * Reset current phase timer
     * @param {Object} currentPhase - Current phase object
     */
    const resetPhaseTimer = (currentPhase) => {
        pauseTimer();
        if (currentPhase) {
            remainingTime.value = currentPhase.maxTime;
            phaseElapsedTimes.value[currentPhaseIndex.value] = 0;
            phasePausedTime = 0;
        }
    };

    /**
     * Move to next phase
     * @param {Array} phases - Array of phases
     * @param {Function} onComplete - Callback when all phases complete
     */
    const nextPhase = (phases, onComplete) => {
        if (currentPhaseIndex.value < phases.length - 1) {
            // Stop current phase timer if running
            if (isRunning.value) {
                pauseTimer();
            }
            
            // Move to next phase
            currentPhaseIndex.value++;
            const nextPhaseObj = phases[currentPhaseIndex.value];
            remainingTime.value = nextPhaseObj.maxTime;
            
            // Reset phase-specific timing
            phasePausedTime = 0;
            
            // Auto-start next phase if meeting has started
            if (hasStarted.value) {
                startTimer(nextPhaseObj);
            }
        } else if (onComplete) {
            pauseTimer();
            onComplete();
        }
    };

    /**
     * Move to previous phase
     * @param {Array} phases - Array of phases
     */
    const previousPhase = (phases) => {
        if (currentPhaseIndex.value > 0) {
            pauseTimer();
            currentPhaseIndex.value--;
            
            const prevPhase = phases[currentPhaseIndex.value];
            const savedElapsedTime = phaseElapsedTimes.value[currentPhaseIndex.value];
            
            remainingTime.value = prevPhase.maxTime - savedElapsedTime;
            phasePausedTime = savedElapsedTime * 1000;
        }
    };

    /**
     * Calculate progress percentage for current phase
     * @param {Object} currentPhase - Current phase object
     * @returns {number} Progress percentage
     */
    const getProgressPercentage = (currentPhase) => {
        if (!currentPhase) return 0;
        const elapsed = currentPhase.maxTime - remainingTime.value;
        return TimeUtils.calculateProgress(elapsed, currentPhase.maxTime);
    };

    // Cleanup on unmount
    onUnmounted(() => {
        if (intervalId) {
            clearInterval(intervalId);
        }
    });

    return {
        // State
        currentPhaseIndex,
        remainingTime,
        isRunning,
        hasStarted,
        phaseElapsedTimes,
        
        // Computed
        timeExpired,
        totalElapsedTime,
        
        // Methods
        initializeTimer,
        toggleTimer,
        resetPhaseTimer,
        nextPhase,
        previousPhase,
        getProgressPercentage
    };
}